import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';

export const useBillingStore = defineStore('billing', () => {
  // State - stores the full usage summary from API
  const subscriptionData = ref(null);
  const usage = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const hasSubscription = computed(() => subscriptionData.value?.has_subscription ?? false);
  
  const currentPlan = computed(() => subscriptionData.value?.plan || null);
  
  const subscription = computed(() => subscriptionData.value?.subscription || null);
  
  const isFreePlan = computed(() => currentPlan.value?.is_free ?? true);
  
  const features = computed(() => subscriptionData.value?.features || {
    private_channels: false,
    presence_channels: false,
    webhooks: false,
  });

  const supportsPrivateChannels = computed(() => features.value.private_channels ?? false);
  const supportsPresenceChannels = computed(() => features.value.presence_channels ?? false);
  const supportsWebhooks = computed(() => features.value.webhooks ?? false);
  
  const limits = computed(() => subscriptionData.value?.limits || null);

  // Actions
  const fetchSubscription = async () => {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await api.get('/billing/subscription');
      // API returns { success: true, data: { has_subscription, plan, subscription, limits, features } }
      subscriptionData.value = response.data.data || response.data;
      return subscriptionData.value;
    } catch (err) {
      // Если нет подписки — это нормально для Free пользователей без подписки
      if (err.response?.status === 404) {
        subscriptionData.value = { has_subscription: false };
        return subscriptionData.value;
      }
      error.value = err.response?.data?.message || 'Ошибка загрузки подписки';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await api.get('/billing/usage');
      usage.value = response.data.data || response.data;
      return usage.value;
    } catch (err) {
      console.error('Failed to fetch usage:', err);
      return null;
    }
  };

  const reset = () => {
    subscriptionData.value = null;
    usage.value = null;
    error.value = null;
  };

  return {
    // State
    subscriptionData,
    subscription,
    usage,
    loading,
    error,
    // Getters
    hasSubscription,
    currentPlan,
    isFreePlan,
    features,
    limits,
    supportsPrivateChannels,
    supportsPresenceChannels,
    supportsWebhooks,
    // Actions
    fetchSubscription,
    fetchUsage,
    reset,
  };
});
